import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import GeneralInfoTab from "./tabs/GeneralInfoTab";
import { useServices } from "../../contexts/Services";
import { useTranslation } from "react-i18next";
import { addOrUpdateService } from "../../db/Services";
import { XMarkIcon, DocumentIcon, PlusIcon } from "@heroicons/react/24/solid";
import Tabs from "../../components/controls/Tabs";
import { useAuth } from "../../contexts/AuthContext";

const ServiceModal = ({ isOpen, onClose, serviceData, onSave }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const categories = useServices();
  const [activeTab, setActiveTab] = useState("general");
  const [formData, setFormData] = useState({
    id: null,
    nameFr: "",
    nameEn: "",
    brand: "",
    description: "",
    price: "",
    tags: [],
    category: "",
    location: {
      formattedAddress: "",
      latitude: null,
      longitude: null,
      regionId: 0,
    },
    imageUrl: "",
    serviceNumber: "",
    isVisible: false,
    priceTypeId: 0,
  });
  const [tempImageUrl, setTempImageUrl] = useState("");
  const [errors, setErrors] = useState({});

  const tabs = [{ id: "general", label: t("modal.generalInfo") }];

  const userId = currentUser?.id;

  useEffect(() => {
    if (serviceData) {
      setFormData({
        id: serviceData.id || null,
        nameFr: serviceData.nameFr || "",
        descriptionFr: serviceData.descriptionFr || "",
        nameEn: serviceData.nameEn || "",
        descriptionEn: serviceData.descriptionEn || "",
        price: serviceData.price || "",
        duration: serviceData.duration || "",
        category: serviceData.category || "",
        location: serviceData.location || {
          formattedAddress: "",
          latitude: null,
          longitude: null,
          regionId: 0,
        },
        imageUrl: serviceData.imageUrl || "",
        serviceNumber: serviceData.serviceNumber || "",
        brand: serviceData.brand || "",
        isVisible: serviceData.isVisible || false,
        priceTypeId: serviceData.priceTypeId || 0,
      });
    } else {
      setFormData({
        id: null,
        nameFr: "",
        descriptionFr: "",
        nameEn: "",
        descriptionEn: "",
        price: "",
        duration: "",
        category: "",
        location: {
          formattedAddress: "",
          latitude: 0,
          longitude: 0,
          regionId: 0,
        },
        imageUrl: "",
        serviceNumber: "",
        brand: "",
        isVisible: false,
        priceTypeId: 0,
      });
    }
  }, [serviceData]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      if (typeof field === "string") {
        return {
          ...prev,
          [field]: value,
        };
      } else if (typeof field === "object") {
        return {
          ...prev,
          ...field,
        };
      }
      return prev;
    });

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Validation des champs
  const validateFields = () => {
    const newErrors = {};
    if (!formData.nameFr.trim()) newErrors.nameFr = t("errors.required");
    if (!formData.descriptionFr.trim())
      newErrors.descriptionFr = t("errors.required");
    if (!formData.nameEn.trim()) newErrors.nameEn = t("errors.required");
    if (!formData.descriptionEn.trim())
      newErrors.descriptionEn = t("errors.required");
    if (!formData.price || formData.price <= 0)
      newErrors.price = t("errors.invalidPrice");
    if (!formData.category) newErrors.category = t("errors.required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    try {
      const payload = {
        ...formData,
        imageUrl: tempImageUrl,
      };

      var item = await addOrUpdateService(payload, userId);

      if (!item) {
        console.error("Erreur : Aucune réponse de l'API.");
        return;
      }

      const updatedPayload = {
        ...payload,
        serviceNumber: item.itemNumber || "N/A",
        id: item.itemId || null,
      };

      await onSave(updatedPayload);
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'ajout/mise à jour du service :", error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={t("modal.titleService")}
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
        content: {
          width: "90%",
          maxWidth: "1500px",
          margin: "auto",
          borderRadius: "12px",
          padding: "0",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          border: 0,
          zIndex: 200,
        },
      }}
    >
      <div className="bg-accent text-white p-4 flex justify-between items-center">
        <h1 className="text-xl">
          {t("modal.titleService")}
          {formData.serviceNumber ? ` - ${formData.serviceNumber}` : ""}
        </h1>
        <XMarkIcon className="w-6 h-6 cursor-pointer" onClick={onClose} />
      </div>
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        fullWidth={true}
      />
      <div className="flex-grow overflow-y-auto p-2 text-primary">
        <form className="p-4 text-primary" onSubmit={handleSubmit}>
          {activeTab === "general" && (
            <GeneralInfoTab
              formData={formData}
              onChange={handleChange}
              errors={errors}
              categories={categories}
              tags={[]}
              onTempImageChange={(url) => {
                setTempImageUrl(url);
              }}
              tempImageUrl={formData?.imageUrl || tempImageUrl || ""}
            />
          )}
        </form>
      </div>
      <div className="sticky bottom-0 bg-accent p-4 flex justify-end gap-4 rounded-b-lg border-t">
        {/* Bouton Annuler */}
        <button
          type="button"
          className="flex items-center gap-2 px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium rounded-full"
          onClick={onClose}
        >
          <XMarkIcon className="w-5 h-5" />
          {t("modal.cancel")}
        </button>

        {/* Bouton Brouillon */}
        <button
          type="button"
          className="flex items-center gap-2 px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium rounded-full hidden"
          onClick={() => console.log("Draft Saved")}
        >
          {t("modal.saveDraft")}
          <DocumentIcon className="w-5 h-5" />
        </button>

        {/* Bouton Enregistrer */}
        <button
          type="button"
          onClick={handleSubmit}
          className="flex items-center gap-2 px-6 py-2 bg-bgCyan hover:hoverCyan text-white font-medium rounded-full"
        >
          {t("modal.save")}
          <PlusIcon className="w-5 h-5" />
        </button>
      </div>
    </Modal>
  );
};

export default ServiceModal;
